import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const QUOTES = [
  "Small touches. Big moments.",
  "Smart moves make busy days feel easy.",
  "Every guest counts—and so do you.",
  "Coaching turns good into great.",
  "Hustle with heart.",
  "The best accessory is great service.",
  "Details drive delight.",
  "Better data. Better days. Better results.",
  "Measure what matters, celebrate what grows.",
  "Make it easy, make it memorable.",
  "Teamwork: the ultimate trend.",
  "Move with purpose. Lead with kindness.",
  "We make retail feel remarkable.",
];

interface AuthPageLayoutProps {
  children: React.ReactNode;
}

const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Math.random() * QUOTES.length));
  const [showQuote, setShowQuote] = useState(true);

  useEffect(() => {
    let transitionTimeout: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      setShowQuote(false);
      transitionTimeout = setTimeout(() => {
        setQuoteIndex((previous) => (previous + 1) % QUOTES.length);
        setShowQuote(true);
      }, 350);
    }, 5000);

    return () => {
      clearInterval(interval);
      if (transitionTimeout) clearTimeout(transitionTimeout);
    };
  }, []);

  return (
    <Box
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50"
      sx={{ py: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 } }}
    >
      <Box
        className="bg-white rounded-2xl shadow-2xl overflow-hidden flex w-full max-w-5xl backdrop-blur-sm"
        sx={{ flexDirection: { xs: "column", md: "row" } }}
      >
        <Box
          className="bg-gradient-to-br from-blue-600 to-primary-light text-white relative overflow-hidden"
          sx={{ p: { xs: 3, sm: 4, md: 6 }, width: { md: "50%" } }}
        >
          <div className="absolute inset-0 opacity-10" aria-hidden="true">
            <div className="absolute top-0 right-0 rtl:right-auto rtl:left-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32 rtl:translate-x-0 rtl:-translate-x-32" />
            <div className="absolute bottom-0 left-0 rtl:left-auto rtl:right-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24 rtl:translate-x-24 rtl:-translate-x-0" />
          </div>
          <div className="flex flex-col h-full relative z-10">
            <div className="flex items-start justify-center">
              <img
                className="w-auto drop-shadow-lg px-4"
                src="/images/Crave Retail Horizontal Stacked - White.png"
                alt={t("login.accessibility.craveRetail")}
              />
            </div>
            <div
              className="mt-4 mb-0 min-h-[64px] flex items-center justify-center px-2 text-center"
              aria-live="polite"
              aria-atomic="true"
              dir="ltr"
            >
              <Box
                key={quoteIndex}
                className={`text-white font-medium font-light transition-[opacity,transform] duration-300 ease-in-out ${showQuote ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
                sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}
              >
                “{QUOTES[quoteIndex]}”
              </Box>
            </div>
            <div className="flex-1" />
            <div className="text-xs flex flex-wrap items-center justify-center gap-4">
              <div>{t("login.text.copyright", { getFullYear: new Date().getFullYear() })}</div>
              <div aria-hidden="true">•</div>
              <div>{t("login.text.version", { appVersion: APP_VERSION })}</div>
              <div aria-hidden="true">•</div>
              <div>
                <a
                  href="https://www.craveretail.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-200"
                >
                  {t("login.text.privacyPolicy")}
                </a>
              </div>
            </div>
          </div>
        </Box>

        <Box
          className="bg-gradient-to-br from-white to-gray-50"
          sx={{ p: { xs: 3, sm: 4, md: 6 }, width: { md: "50%" } }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AuthPageLayout;
